import type { NodeDataType } from './GraphType';

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Node, NodeProps } from '@xyflow/react';
import { Handle, Position } from '@xyflow/react';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { DangerFillIcon } from '@icons/DangerFillIcon';
import { WizardIcon } from '@icons/WizardIcon';
import { TriggerIcon } from '../trigger/TriggerIcon';

// Node type with custom data for v12 compatibility
type TriggerNodeType = Node<NodeDataType, 'trigger'>;
type Props = NodeProps<TriggerNodeType>;

export const TriggerNode = observer((node: Props) => {
  const { data, isConnectable: isNodeConnectable } = node;
  const { t } = useTranslation();
  const { workflowDetailStore } = useStore();
  const isEditable = workflowDetailStore.checkEditable();
  const isConnectable = isNodeConnectable && isEditable;
  const isSelected = workflowDetailStore.curNodeId === node.id;
  const handleClickTriggerBehavior = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    workflowDetailStore.curNodeId = node.id;
    if (isEditable) {
      workflowDetailStore.setToolbarView('triggerSelect');
    } else {
      workflowDetailStore.setToolbarView('triggerConfig');
    }
  };

  // const handleClickTriggerBehaviorConfig = (e: React.MouseEvent<HTMLDivElement>) => {
  //   e.stopPropagation();
  //   workflowDetailStore.curNodeId = node.id;
  //   workflowDetailStore.setToolbarView('triggerConfig');
  // };

  const handleClickNode = () => {
    workflowDetailStore.curNodeId = node.id;
    workflowDetailStore.setToolbarView(isEditable ? 'triggerSelect' : 'triggerConfig');
  };

  const triggerName = data.baseTriggerModel?.name;

  // 트리거 설정이 완료되지 않았는지 체크
  const isTriggerConfigInvalid = () => {
    const baseTrigger = data.baseTriggerModel;
    const triggerModel = data.triggerModel;

    // 설정이 필요한 트리거인 경우
    if (baseTrigger && baseTrigger.isConfigurable) {
      // 스케줄러 트리거는 기본값이 있으므로 항상 유효함
      if (baseTrigger.type === 'SCHEDULER_TRIGGER') {
        return false;
      }

      const conditions = triggerModel?.conditions;

      // conditions가 없거나 비어있음
      if (!conditions || conditions.length === 0) return true;

      // 모든 조건이 채워져있는지 확인
      return conditions.some(condition => !condition.fieldName || condition.value === undefined);
    }

    return false;
  };

  const showDangerIcon = isTriggerConfigInvalid();

  return (
    <Container isSelected={isSelected} onClick={handleClickNode}>
      <Type>
        <span>
          <WizardIcon width={16} height={16} />
          <span>{t('Workflow_Create_02')}</span>
        </span>
        {showDangerIcon && (
          <Tooltip title={t('Workflow_Validation_RequiredFieldsNotSet')} placement="bottom" arrow>
            <DangerIconWrapper>
              <DangerFillIcon width={16} height={16} />
            </DangerIconWrapper>
          </Tooltip>
        )}
      </Type>
      <Behavior
        isSelected={isSelected && workflowDetailStore.toolbar === 'triggerSelect'}
        hasTriggerName={!!triggerName}
        onClick={handleClickTriggerBehavior}
      >
        <BehaviorTitleBox>
          {data.baseTriggerModel?.eventName && (
            <TriggerIcon triggerName={data.baseTriggerModel?.eventName} width={24} height={24} />
          )}
          {triggerName || t('Workflow_Create_03')}
        </BehaviorTitleBox>
      </Behavior>
      {/* {!!data.baseTriggerModel?.isConfigurable && (
        <Config 
          
        `
          onClick={handleClickTriggerBehaviorConfig}
          isSelected={isSelected && workflowDetailStore.toolbar === 'triggerConfig'}
        >
          {t('Setting')}
        </Config>
      )} */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="normal"
        isConnectable={isConnectable}
        style={{
          width: 8,
          height: 8,
          background: '#fff',
          border: '2px solid #9CA3AF',
        }}
      />
    </Container>
  );
});

export const MemoizedMovie = React.memo(TriggerNode);

const Container = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  border: ${props =>
    props.isSelected
      ? `2px solid ${props.theme.colors['border-primary']}`
      : `1px solid ${props.theme.colors['border-gray-lighter']}`};
  outline: ${props => (props.isSelected ? `4px solid ${props.theme.colors['border-primary-light']}` : 'none')};
  height: fit-content;
  width: '392px';
  background: ${props => props.theme.colors['bg-gray-light']};
  border-radius: 4px;
  padding: ${props => (props.isSelected ? '7px 7px 0px 7px' : '8px 8px 0px 8px')};
  box-shadow: ${props =>
    props.isSelected
      ? '0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04), 0px 6px 30px 5px rgba(0, 0, 0, 0.05)'
      : 'none'};
`;

const Type = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0px 4px;

  ${fonts.Headline9};
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  justify-content: space-between;
  & > span {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
  }
`;

const Behavior = styled.div<{ isSelected: boolean; hasTriggerName: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 40px;
  border-radius: 3px;
  border-bottom-left-radius: ${props => (props.isSelected ? '0px' : '3px')};
  border-bottom-right-radius: ${props => (props.isSelected ? '0px' : '3px')};
  padding: 0px 12px;
  margin: 8px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  background: ${props => props.theme.colors['bg-white']};

  ${props => (props.isSelected ? fonts.Headline8 : fonts.Body2)};
  color: ${props =>
    props.isSelected
      ? props.theme.colors['text-primary']
      : props.hasTriggerName
      ? props.theme.colors['text-gray-main']
      : props.theme.colors['text-gray-light']};
`;

const BehaviorTitleBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const DangerIconWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

// const Config = styled.div<{ isSelected: boolean }>`
//   display: flex;
//   flex-direction: row;
//   align-items: center;
//   height: 40px;
//   border-radius: 3px;
//   border-top-left-radius: 0px;
//   border-top-right-radius: 0px;
//   padding: 0px 16px;
//   border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
//   border-top: 0px;
//   background: ${props => props.theme.colors['bg-white']};
//   ${props => (props.isSelected ? fonts.Headline8 : fonts.Body2)};
//   color: ${props => (props.isSelected ? props.theme.colors['text-primary'] : props.theme.colors['text-gray-main'])};
// `;
